/**
 * Homepage Tiles Service
 * 
 * CRUD operations for the Quick Access Navigation tiles on the homepage.
 */

import { safeFrom, isSupabaseAvailable, getSupabase } from '@/lib/safe-supabase';
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError
} from '@/lib/service-errors';
import { auditContent } from '@/lib/audit-logger';

export type HomepageTile = {
  id: number;
  title: string;
  description: string | null;
  icon: string;
  link: string;
  cta_text: string | null;
  color_class: string | null;
  order_index: number;
  published: boolean;
  created_at: string;
  updated_at: string;
};

export type HomepageTileInput = {
  title: string;
  description?: string;
  icon: string;
  link: string;
  cta_text?: string;
  color_class?: string;
  order_index?: number;
  published?: boolean;
};

// Available icons for the tile selector
export const availableIcons = [
  'Globe',
  'BookOpen',
  'Users',
  'Calendar',
  'Heart',
  'Music',
  'Award',
  'Star',
  'Target',
  'Sparkles',
  'Building2',
  'Handshake',
  'GraduationCap',
  'Camera',
  'Megaphone',
  'Gift',
] as const;

// Available color classes
export const availableColorClasses = [
  { label: 'Heritage (Orange)', value: 'bg-heritage/10 hover:bg-heritage/20 border-heritage/20' },
  { label: 'Culture (Teal)', value: 'bg-culture/10 hover:bg-culture/20 border-culture/20' },
  { label: 'Accent (Gold)', value: 'bg-accent/10 hover:bg-accent/20 border-accent/20' },
  { label: 'Primary (Dark)', value: 'bg-primary/10 hover:bg-primary/20 border-primary/20' },
  { label: 'Secondary (Light)', value: 'bg-secondary hover:bg-secondary/80 border-secondary' },
] as const;

/**
 * Fetch all published tiles (for public display)
 */
export const fetchPublishedTiles = async (): Promise<ServiceResponse<HomepageTile[]>> => {
  if (!isSupabaseAvailable()) {
    return successResponse([]);
  }

  try {
    const { data, error } = await safeFrom('homepage_tiles')
      .select('*')
      .eq('published', true)
      .order('order_index', { ascending: true });

    if (error) {
      // If table doesn't exist (404), return empty array instead of error
      const errorMessage = error.message?.toLowerCase() || '';
      const errorCode = (error as any).code || (error as any).status || (error as any).statusCode;
      
      if (
        errorCode === 'PGRST116' || 
        errorCode === 404 || 
        errorCode === '404' ||
        errorMessage.includes('does not exist') || 
        errorMessage.includes('relation') ||
        errorMessage.includes('not found') ||
        errorMessage.includes('404')
      ) {
        return successResponse([]);
      }
      throw error;
    }

    return successResponse((data || []) as HomepageTile[]);
  } catch (error) {
    // Handle table not existing gracefully
    const errorMessage = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
    const errorCode = (error as any)?.code || (error as any)?.status || (error as any)?.statusCode;
    
    if (
      errorCode === 404 || 
      errorCode === '404' ||
      errorMessage.includes('does not exist') || 
      errorMessage.includes('relation') ||
      errorMessage.includes('not found') ||
      errorMessage.includes('404')
    ) {
      return successResponse([]);
    }
    return errorResponse(handleServiceError(error, 'homepageTilesService.fetchPublishedTiles'));
  }
};

/**
 * Fetch all tiles (for admin)
 */
export const fetchAllTiles = async (): Promise<ServiceResponse<HomepageTile[]>> => {
  if (!isSupabaseAvailable()) {
    return successResponse([]);
  }

  try {
    const { data, error } = await safeFrom('homepage_tiles')
      .select('*')
      .order('order_index', { ascending: true });

    if (error) {
      // If table doesn't exist (404), return empty array instead of error
      const errorMessage = (error.message || String(error) || '').toLowerCase();
      const errorCode = (error as any).code || (error as any).status || (error as any).statusCode || (error as any).hint;
      const errorDetails = JSON.stringify(error).toLowerCase();
      
      // Check for various 404 indicators
      if (
        errorCode === 'PGRST116' || 
        errorCode === 404 || 
        errorCode === '404' ||
        errorCode === '42P01' || // PostgreSQL "relation does not exist"
        errorMessage.includes('does not exist') || 
        errorMessage.includes('relation') ||
        errorMessage.includes('not found') ||
        errorMessage.includes('404') ||
        errorDetails.includes('404') ||
        errorDetails.includes('does not exist') ||
        errorDetails.includes('relation')
      ) {
        // Table doesn't exist - return empty array silently
        return successResponse([]);
      }
      throw error;
    }

    return successResponse((data || []) as HomepageTile[]);
  } catch (error) {
    // Handle table not existing gracefully
    const errorMessage = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
    const errorCode = (error as any)?.code || (error as any)?.status || (error as any)?.statusCode;
    const errorDetails = JSON.stringify(error).toLowerCase();
    
    if (
      errorCode === 404 || 
      errorCode === '404' ||
      errorCode === '42P01' ||
      errorMessage.includes('does not exist') || 
      errorMessage.includes('relation') ||
      errorMessage.includes('not found') ||
      errorMessage.includes('404') ||
      errorDetails.includes('404') ||
      errorDetails.includes('does not exist')
    ) {
      // Table doesn't exist - return empty array silently
      return successResponse([]);
    }
    return errorResponse(handleServiceError(error, 'homepageTilesService.fetchAllTiles'));
  }
};

/**
 * Create a new tile
 */
export const createTile = async (tile: HomepageTileInput): Promise<ServiceResponse<HomepageTile>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(handleServiceError(new Error('Supabase not configured'), 'homepageTilesService.createTile'));
  }

  try {
    // Check for duplicates before creating
    const existingResult = await fetchAllTiles();
    if (existingResult.success) {
      const duplicate = existingResult.data.find(
        t => t.title === tile.title && t.link === tile.link
      );
      if (duplicate) {
        return errorResponse(handleServiceError(
          new Error(`A tile with title "${tile.title}" and link "${tile.link}" already exists.`),
          'homepageTilesService.createTile'
        ));
      }
    }

    const { data, error } = await safeFrom('homepage_tiles')
      .insert([tile])
      .select()
      .single();

    if (error) {
      throw error;
    }

    const created = data as HomepageTile;
    
    // Audit log
    await auditContent.create('homepage_tile', created.id.toString(), {
      title: created.title,
    });

    return successResponse(created);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'homepageTilesService.createTile'));
  }
};

/**
 * Update a tile
 */
export const updateTile = async (id: number, tile: Partial<HomepageTileInput>): Promise<ServiceResponse<HomepageTile>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(handleServiceError(new Error('Supabase not configured'), 'homepageTilesService.updateTile'));
  }

  try {
    // First, get the current tile to compare values
    const { data: currentTileData, error: currentError } = await safeFrom('homepage_tiles')
      .select('id, title, link')
      .eq('id', id)
      .single();
    
    if (currentError || !currentTileData) {
      return errorResponse(handleServiceError(
        new Error('Tile not found'),
        'homepageTilesService.updateTile'
      ));
    }
    
    const currentTile = currentTileData as { id: number; title: string; link: string };
    
    // Determine what the new values will be
    const newTitle = tile.title !== undefined ? tile.title : currentTile.title;
    const newLink = tile.link !== undefined ? tile.link : currentTile.link;
    
    // Only check for duplicates if title or link is actually being changed
    const titleChanged = tile.title !== undefined && tile.title !== currentTile.title;
    const linkChanged = tile.link !== undefined && tile.link !== currentTile.link;
    
    if (titleChanged || linkChanged) {
      // Fetch ALL tiles (including unpublished) for admin validation
      const { data: allTiles, error: fetchError } = await safeFrom('homepage_tiles')
        .select('id, title, link')
        .order('order_index', { ascending: true });
      
      if (fetchError) {
        throw fetchError;
      }
      
      if (allTiles) {
        // Check if another tile (not the current one) has the same title and link
        const duplicate = allTiles.find(
          (t: any) => t.id !== id && t.title === newTitle && t.link === newLink
        );
        
        if (duplicate) {
          return errorResponse(handleServiceError(
            new Error(`A tile with title "${newTitle}" and link "${newLink}" already exists.`),
            'homepageTilesService.updateTile'
          ));
        }
      }
    }

    // Ensure we're only updating the specified fields
    const updateData: any = {};
    if (tile.title !== undefined) updateData.title = tile.title;
    if (tile.description !== undefined) updateData.description = tile.description;
    if (tile.icon !== undefined) updateData.icon = tile.icon;
    if (tile.link !== undefined) updateData.link = tile.link;
    if (tile.cta_text !== undefined) updateData.cta_text = tile.cta_text;
    if (tile.color_class !== undefined) updateData.color_class = tile.color_class;
    if (tile.order_index !== undefined) updateData.order_index = tile.order_index;
    if (tile.published !== undefined) updateData.published = tile.published;

    const { data, error } = await safeFrom('homepage_tiles')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw error;
    }

    const updated = data as HomepageTile;
    
    // Audit log
    await auditContent.update('homepage_tile', id.toString(), {}, {
      title: updated.title,
    });

    return successResponse(updated);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'homepageTilesService.updateTile', { id }));
  }
};

/**
 * Delete a tile
 */
export const deleteTile = async (id: number): Promise<ServiceResponse<boolean>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(handleServiceError(new Error('Supabase not configured'), 'homepageTilesService.deleteTile'));
  }

  try {
    const { error } = await safeFrom('homepage_tiles')
      .delete()
      .eq('id', id);

    if (error) {
      throw error;
    }

    // Audit log
    await auditContent.delete('homepage_tile', id.toString(), { id });

    return successResponse(true);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'homepageTilesService.deleteTile', { id }));
  }
};

/**
 * Reorder tiles
 */
export const reorderTiles = async (tiles: { id: number; order_index: number }[]): Promise<ServiceResponse<boolean>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(handleServiceError(new Error('Supabase not configured'), 'homepageTilesService.reorderTiles'));
  }

  try {
    const supabase = getSupabase();
    
    // Update each tile's order_index
    const updates = tiles.map(({ id, order_index }) =>
      supabase
        .from('homepage_tiles')
        .update({ order_index })
        .eq('id', id)
    );

    const results = await Promise.all(updates);
    const errors = results.filter(r => r.error);

    if (errors.length > 0) {
      console.error('Error reordering tiles:', errors);
      throw new Error('Failed to reorder some tiles');
    }

    return successResponse(true);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'homepageTilesService.reorderTiles'));
  }
};


